import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import { selectUser } from "../../components/auth/auth.selector";
import HelmetWrapper from "../../HelmetWrapper";
import {
  fetchBCALogsData,
  fetchBCAStatsData,
  fetchFitnessProfileLogsData,
  fetchFitnessProfileStatsData,
} from "../../redux/prospect-reducers/prospectSlice";
import CommonStructured from "../../components/common-components/CommonStructured";
import FitnessProfileTabs from "../../components/fitness-profile/FitnessProfileTabs";
import "../../styles/fitness-profile/fitness-profile-logs.css";

const FitnessProfileLogs = () => {
  const dispatch = useDispatch();
  const userInfoData = useSelector(selectUser);

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isMarketing = userInfoData?.account_type === "marketing";
  // const isClusterManager = userInfoData?.account_type === "cluster_manager";
  const isManagerRole = [
    "fitness_manager",
    "sales_manager",
    "club_manager",
  ].includes(userInfoData?.account_type);

  const [pageLimit, setPageLimit] = useState(10);
  const [isPage, setIsPage] = useState(0);
  const iniAllFilters = {
    selectedGym:
      !isAdmin && !isMarketing && !isManagerRole
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedRegularPlanStatus: "",
    selectedPTPlanStatus: "",
    isActive: "",
    selectedTrainer: "",
    leadAddDateFrom: "",
    leadAddDateTo: "",
    scheduledDateFrom: "",
    scheduledDateTo: "",
    searchString: "",
    fitness_milestone: "",
    pending_days_range: "",
  };
  const [allFilters, setAllFilters] = useState(iniAllFilters);
  const iniFilters = {
    limit: pageLimit || 10,
    page: isPage + 1,
    gym_id: allFilters.selectedGym?.value || "",
    regular_plan_status: allFilters.selectedRegularPlanStatus?.value || "",
    pt_plan_status: allFilters.selectedPTPlanStatus?.value || "",
    status: allFilters.isActive?.value || "",
    trainer_id: allFilters.selectedTrainer?.value || "",
    start_date: allFilters.leadAddDateFrom || "",
    end_date: allFilters.leadAddDateTo || "",
    scheduled_date_start: allFilters.scheduledDateFrom || "",
    scheduled_date_end: allFilters.scheduledDateTo || "",
    fitness_milestone: "",
    pending_days_range: "",
  };
  const [filters, setFilters] = useState(iniFilters);
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);

  const onResetFilters = () => {
    setAllFilters(iniAllFilters);
    setFilters(iniFilters);
  };

  const onClickSearch = () => {
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prevs) => ({ ...prevs, search: allFilters.searchString }));
    }
  };

  function getFitnessProfileLogs() {
    const {
      pending_days_range,
      scheduled_date_start,
      scheduled_date_end,
      ...logsFilters
    } = filters;

    dispatch(
      fetchFitnessProfileLogsData({
        filters: logsFilters,
        token: userInfoData?.token,
      })
    );
  }

  function getBCALogs() {
    const { fitness_milestone, ...logsFilters } = filters;

    dispatch(
      fetchBCALogsData({
        filters: logsFilters,
        token: userInfoData?.token,
      })
    );
  }

  function getBCAStats() {
    const {
      limit,
      page,
      pending_days_range,
      fitness_milestone,
      ...statsFilters
    } = filters;

    dispatch(
      fetchBCAStatsData({
        filters: statsFilters,
        token: userInfoData?.token,
      })
    );
  }

  function getFitnessProfileStats() {
    const {
      limit,
      page,
      pending_days_range,
      fitness_milestone,
      scheduled_date_start,
      scheduled_date_end,
      ...statsFilters
    } = filters;

    dispatch(
      fetchFitnessProfileStatsData({
        filters: statsFilters,
        token: userInfoData?.token,
      })
    );
  }

  function getFitnessProfileLogsAndBCALogs() {
    getFitnessProfileLogs(filters);
    getBCALogs(filters);
    getBCAStats(filters);
    getFitnessProfileStats(filters);
  }

  useEffect(() => {
    getBCALogs(filters);
  }, [
    filters.limit,
    filters.page,
    filters.gym_id,
    filters.regular_plan_status,
    filters.pt_plan_status,
    filters.status,
    filters.trainer_id,
    filters.start_date,
    filters.end_date,
    filters.scheduled_date_start,
    filters.scheduled_date_end,
    filters.pending_days_range,
  ]);

  useEffect(() => {
    getFitnessProfileLogs(filters);
  }, [
    filters.limit,
    filters.page,
    filters.gym_id,
    filters.regular_plan_status,
    filters.pt_plan_status,
    filters.status,
    filters.trainer_id,
    filters.start_date,
    filters.end_date,
    filters.fitness_milestone,
  ]);

  useEffect(() => {
    getFitnessProfileStats(filters);
  }, [
    filters.gym_id,
    filters.regular_plan_status,
    filters.pt_plan_status,
    filters.status,
    filters.trainer_id,
    filters.start_date,
    filters.end_date,
  ]);

  useEffect(() => {
    getBCAStats(filters);
  }, [
    filters.gym_id,
    filters.regular_plan_status,
    filters.pt_plan_status,
    filters.status,
    filters.trainer_id,
    filters.start_date,
    filters.end_date,
    filters.scheduled_date_start,
    filters.scheduled_date_end,
  ]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      limit: pageLimit || 10,
      page: isPage + 1,
      gym_id: allFilters.selectedGym?.value || "",
      regular_plan_status: allFilters.selectedRegularPlanStatus?.value || "",
      pt_plan_status: allFilters.selectedPTPlanStatus?.value || "",
      status: allFilters.isActive?.value || "",
      trainer_id: allFilters.selectedTrainer?.value || "",
      start_date: allFilters.leadAddDateFrom || "",
      end_date: allFilters.leadAddDateTo || "",
      scheduled_date_start: allFilters.scheduledDateFrom || "",
      scheduled_date_end: allFilters.scheduledDateTo || "",
      fitness_milestone: allFilters.fitness_milestone || "",
      pending_days_range: allFilters.pending_days_range || "",
    }));
  }, [
    pageLimit,
    isPage,
    allFilters.selectedGym,
    allFilters.selectedRegularPlanStatus,
    allFilters.selectedPTPlanStatus,
    allFilters.isActive,
    allFilters.selectedTrainer,
    allFilters.leadAddDateFrom,
    allFilters.leadAddDateTo,
    allFilters.scheduledDateFrom,
    allFilters.scheduledDateTo,
    allFilters.fitness_milestone,
    allFilters.pending_days_range,
  ]);

  useEffect(() => {
    if (allFilters.searchString?.length === 0) {
      setSearchFilterApplied(false);
      setFilters((prevs) => ({ ...prevs, search: "" }));
    }
  }, [allFilters.searchString]);

  return (
    <HelmetWrapper title="WTF: Member Fitness Profile Logs">
      <Container className="bg-cloud-k px-50px" fluid>
        <Row>
          <Col>
            <CommonStructured
              pageHeading={"Member Fitness Profile & BCA Logs"}
              isReload={() => getFitnessProfileLogsAndBCALogs()}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <FitnessProfileTabs
              userInfoData={userInfoData}
              isPage={isPage}
              setIsPage={setIsPage}
              pageLimit={pageLimit}
              setPageLimit={setPageLimit}
              allFilters={allFilters}
              setAllFilters={setAllFilters}
              onClickSearch={onClickSearch}
              searchFilterApplied={searchFilterApplied}
              onClickReset={onResetFilters}
              onResetFilters={onResetFilters}
            />
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default FitnessProfileLogs;
